import { api, setAuthToken } from './api-client';

// Base64URL encoding/decoding helpers
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLen);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Convert PublicKeyCredentialCreationOptions from server format to browser format
function preparePublicKeyOptions(options: any): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    excludeCredentials: options.excludeCredentials?.map((cred: any) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    })),
  };
}

// Convert PublicKeyCredentialRequestOptions from server format to browser format
function preparePublicKeyRequestOptions(options: any): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: options.allowCredentials?.map((cred: any) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    })),
  };
}

// Register a new WebAuthn credential
export async function registerWithWebAuthn(
  username: string,
  displayName: string
): Promise<{
  sessionToken: string;
  user: { username: string; displayName: string; role: string };
}> {
  // Step 1: Get registration options from server
  const { publicKey, sessionId } = await api.post<{
    publicKey: any;
    sessionId: string;
  }>('/auth/webauthn/register/options', { username, displayName });

  // Step 2: Create credential using WebAuthn API
  const options = preparePublicKeyOptions(publicKey);
  const credential = (await navigator.credentials.create({
    publicKey: options,
  })) as PublicKeyCredential;

  if (!credential) {
    throw new Error('Failed to create credential');
  }

  const response = credential.response as AuthenticatorAttestationResponse;

  // Step 3: Send credential to server for verification
  const verificationData = {
    username,
    sessionId,
    credential: {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        attestationObject: bufferToBase64url(response.attestationObject),
      },
    },
  };

  const result = await api.post<{
    sessionToken: string;
    user: { username: string; displayName: string; role: string }
  }>(
    '/auth/webauthn/register/verify',
    verificationData
  );

  // Store the session token and user info
  if (result.sessionToken) {
    setAuthToken(result.sessionToken);
    if (result.user) {
      localStorage.setItem('user', JSON.stringify(result.user));
    }
  }

  return result;
}

// Login with existing WebAuthn credential
export async function loginWithWebAuthn(
  username: string
): Promise<{
  sessionToken: string;
  user: { username: string; displayName: string; role: string };
}> {
  // Step 1: Get login challenge from server
  const { publicKey, sessionId } = await api.post<{
    publicKey: any;
    sessionId: string;
  }>('/auth/webauthn/login/options', { username });

  // Step 2: Sign challenge using WebAuthn API
  const options = preparePublicKeyRequestOptions(publicKey);
  const credential = (await navigator.credentials.get({
    publicKey: options,
  })) as PublicKeyCredential;

  if (!credential) {
    throw new Error('Failed to get credential');
  }

  const response = credential.response as AuthenticatorAssertionResponse;

  // Step 3: Send signed challenge to server for verification
  const verificationData = {
    username,
    sessionId,
    credential: {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        authenticatorData: bufferToBase64url(response.authenticatorData),
        signature: bufferToBase64url(response.signature),
        userHandle: response.userHandle
          ? bufferToBase64url(response.userHandle)
          : undefined,
      },
    },
  };

  const result = await api.post<{
    sessionToken: string;
    user: { username: string; displayName: string; role: string };
  }>(
    '/auth/webauthn/login/verify',
    verificationData
  );

  // Store the session token and user info
  if (result.sessionToken) {
    setAuthToken(result.sessionToken);
    if (result.user) {
      localStorage.setItem('user', JSON.stringify(result.user));
    }
  }

  return result;
}

// Fetch allowed credentials for the current authenticated user
// This ensures only the logged-in user's credentials can be used for signatures
export async function getAllowedCredentials(): Promise<PublicKeyCredentialDescriptor[]> {
  try {
    const response = await api.get<{ allowCredentials: any[] }>('/auth/webauthn/allowed-credentials');

    if (!response.allowCredentials || response.allowCredentials.length === 0) {
      throw new Error('No credentials found for current user');
    }

    // Convert credential IDs from base64url to ArrayBuffer
    return response.allowCredentials.map((cred: any) => ({
      type: cred.type || 'public-key',
      id: base64urlToBuffer(cred.id),
      transports: cred.transports,
    }));
  } catch (err) {
    console.error('Failed to fetch allowed credentials:', err);
    throw new Error('Failed to fetch user credentials. Please try logging in again.');
  }
}

// Check if WebAuthn is supported
export function isWebAuthnSupported(): boolean {
  return (
    window.PublicKeyCredential !== undefined &&
    navigator.credentials !== undefined
  );
}
